import React, { useCallback, useRef, useState } from 'react';

const DEFAULT_ACCEPTED = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx', '.csv'];

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i).toLowerCase();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

function FileTypeIcon({ name }: { name: string }) {
  const ext = extOf(name).replace('.', '');
  const label = ext === 'xlsx' || ext === 'xls' ? 'XLS' : (ext || 'FILE').toUpperCase();
  return (
    <span className="upload-file-icon" aria-hidden="true">
      {label}
    </span>
  );
}

interface FileDropzoneProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  acceptedExtensions?: string[];
  disabled?: boolean;
  /** Reset key: change it to force-clear the underlying <input>. */
  resetKey?: string | number;
}

/**
 * Drag-and-drop file picker with click-to-browse, keyboard support, extension
 * validation, and a selected-file chip. Reuses the `upload-*` CSS classes from
 * `src/index.css` so it matches the livestock importer's dropzone.
 */
const FileDropzone: React.FC<FileDropzoneProps> = ({
  file,
  onFileChange,
  acceptedExtensions = DEFAULT_ACCEPTED,
  disabled = false,
  resetKey,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Callers pass 'pdf' or '.pdf' in either case; compare on one shape so a
  // missing dot never turns a valid invoice away.
  const accepted = acceptedExtensions.map((ext) => (ext.startsWith('.') ? ext : `.${ext}`).toLowerCase());
  const isAccepted = useCallback(
    (name: string) => accepted.length === 0 || accepted.includes(extOf(name)),
    [accepted],
  );

  const accept = useCallback(
    (candidate: File) => {
      if (!isAccepted(candidate.name)) {
        setError(`"${candidate.name}" isn't a supported file type. Allowed: ${accepted.map((e) => e.slice(1)).join(', ')}.`);
        return;
      }
      setError(null);
      onFileChange(candidate);
    },
    [isAccepted, accepted, onFileChange],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragActive(false);
      if (disabled) return;
      const dropped = e.dataTransfer.files?.[0];
      if (dropped) accept(dropped);
    },
    [accept, disabled],
  );

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) accept(f);
    e.target.value = '';
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }

  function remove() {
    setError(null);
    onFileChange(null);
  }

  return (
    <div className="grid gap-1">
      {error && <div className="alert error text-sm" role="alert">{error}</div>}
      {!file ? (
        <div
          className={`upload-dropzone${isDragActive ? ' is-drag-active' : ''}`}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label="Choose a file to upload, or drag and drop it here"
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={onKeyDown}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragActive(true); }}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={onDrop}
        >
          <span className="upload-dropzone-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 16V4M12 4L7 9M12 4L17 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 16.5V18a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="upload-dropzone-title">Drop your document here</span>
          <span className="upload-dropzone-sub">or click to browse — {accepted.map((e) => e.slice(1)).join(', ')}</span>
          <input
            key={resetKey}
            ref={inputRef}
            type="file"
            accept={accepted.join(',')}
            onChange={onInputChange}
            className="upload-hidden-input"
            aria-hidden="true"
            tabIndex={-1}
            disabled={disabled}
          />
        </div>
      ) : (
        <div className="upload-file-chip">
          <FileTypeIcon name={file.name} />
          <span className="upload-file-meta">
            <span className="upload-file-name" title={file.name}>{file.name}</span>
            <span className="upload-file-size">{formatBytes(file.size)}</span>
          </span>
          <button
            type="button"
            className="upload-file-remove"
            onClick={remove}
            aria-label={`Remove ${file.name}`}
            disabled={disabled}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
